import React, { useState, useEffect, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { makeStyles } from '@mui/styles';
import { Paper, Grid, Box, Button, Avatar } from '@mui/material';
import Controls from '../components/controls/Controls';
import * as MyCollections from "../services/MyCollections";
import { urlAccountingSettlement, urlSupervisors, urlFiles, urlAccountingStatements } from '../config';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import * as CommonFunctions from '../services/CommonFunctions';
import CustomTooltip from '../components/CustomTooltip';
import MyPDFContent from '../components/MyPDFContent';
import html2pdf from 'html2pdf.js';
import ReactDOMServer from 'react-dom/server'; // Dodaj ten import
import { fetchDataPromise } from '../services/ApiServices';
import ApproveAdditionalCosts from '../components/ApproveAdditionalCostsDialog';
import { urlAdditionalCosts } from '../config';
import { logActivity } from '../services/ApiServices';
import { logActionType } from "../services/MyCollections";
import { useThemeContext } from '../components/contexts/ThemeContext';

const useStyles = makeStyles(theme => ({
  pageContent: {
    marginTop: 20
  },
}))


const AccountantAvatarRenderer = (props) => {
  const fullName = props.value || '';
  const parts = fullName.trim().split(' ');
  const surname = parts[0] || '';
  const name = parts[1] || '';
  const initials = `${surname.charAt(0) || ''}${name.charAt(0) || ''}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '10px', padding: '4px 0', justifyContent: 'flex-start' }}>
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: '11px',
          bgcolor: props.context?.darkTheme ? '#feb33c' : '#ffe599',
          color: 'black',
          fontWeight: 'bold'
        }}
      >
        {initials}
      </Avatar>
      <span style={{ fontWeight: '600', color: props.context?.darkTheme ? 'white' : 'inherit' }}>{fullName}</span>
    </div>
  );
};

function ApproveStatements() {
  const { roles: roles } = useAuthStore();

  const token = useAuthStore().token;
  const contactId = useAuthStore().contactId;
  const userName = useAuthStore().fullName;
  const [gridApi, setGridApi] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    String(parseInt(CommonFunctions.getMonthForSelect(), 10))
  );
  const [selectedYear, setSelectedYear] = useState(
    String(parseInt(CommonFunctions.getYearForSelect(), 10))
  );
  const [unlockedMonthLimit, setUnlockedMonthLimit] = useState(() => {
    const defaultLim = parseInt(CommonFunctions.getMonthForSelect(), 10);
    const initialMonth = parseInt(CommonFunctions.getMonthForSelect(), 10);
    return Math.max(defaultLim, initialMonth);
  });
  const [selectedSupervisor, setSelectedSupervisor] = useState(roles?.includes('SUPERVISOR') || roles?.includes('SUPERVISOR_ACCOUNTANT') ? contactId : "0");
  const classes = useStyles();
  const navigate = useNavigate();
  const [supervisors, setSupervisors] = useState([]);
  const [accountingSettlement, setAccountingSettlement] = useState([]);
  const [clients, setClients] = useState([]);
  const [url, setUrl] = useState('');
  const [accountantId, setAccountantId] = useState(null);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [currentRecordForApproval, setCurrentRecordForApproval] = useState([]);
  const isApproveButtonAllowed = roles?.includes('MANAGER') || roles?.includes('ADMIN') || roles?.includes('ADMIN_SUPERVISOR') || roles?.includes('SUPERVISOR') || roles?.includes('SUPERVISOR_ACCOUNTANT');
  const [currentDate, setCurrentDate] = useState(CommonFunctions.getCurrentDate())
  const { mode, toggleTheme } = useThemeContext();
  const darkTheme = mode === 'dark';

  // Each Column Definition results in one Column.
  const columnDefs = useMemo(() => [
    {
      headerName: "Twoje Księgowe i Księgowi",
      field: "accountantName",
      sortable: true,
      lockPosition: 'left',
      filter: 'agTextColumnFilter',
      minWidth: 200,
      tooltipValueGetter: (params) => params.data.accountantCompanyName,
      cellRenderer: AccountantAvatarRenderer,
    },
    {
      headerName: "Status Zestawienia",
      field: "statementApproved",
      lockPosition: 'left',
      minWidth: 200,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: function (params) {
        const { statementApproved, approvalDate, finishDate, isEdited, statementFinished } = params.data;

        let label = "";
        let icon = "";
        let bgColor = "";
        let textColor = "";

        if (statementApproved) {
          const daysDifference = CommonFunctions.calculateDaysDifference(finishDate, approvalDate);
          label = `zatwierdzone ${approvalDate} (${daysDifference}D)`;
          icon = "fas fa-check-circle";
          bgColor = darkTheme ? "rgba(46, 125, 50, 0.2)" : "#e8f5e9";
          textColor = darkTheme ? "#81c784" : "#2e7d32";
        } else if (statementFinished) {
          label = isEdited ? `wysłane ${finishDate} (E)` : `wysłane ${finishDate}`;
          icon = "fas fa-paper-plane";
          bgColor = darkTheme ? "rgba(116, 27, 71, 0.2)" : "#fce4ec";
          textColor = darkTheme ? "#f48fb1" : "#741b47";
        } else {
          if (isEdited) {
            label = "w edycji...";
            icon = "fas fa-edit";
            bgColor = darkTheme ? "rgba(253, 179, 60, 0.2)" : "#fff3e0";
            textColor = darkTheme ? "#ffb74d" : "#e65100";
          } else {
            label = "oczekuje...";
            icon = "fas fa-hourglass-start";
            bgColor = darkTheme ? "rgba(255, 255, 255, 0.05)" : "#f5f5f5";
            textColor = darkTheme ? "#9e9e9e" : "#616161";
          }
        }

        return (
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 12px",
            borderRadius: "16px",
            backgroundColor: bgColor,
            color: textColor,
            fontWeight: "600",
            fontSize: "12px",
            border: `1px solid ${textColor}40`,
            lineHeight: "1"
          }}>
            <i className={icon}></i>
            <span>{label}</span>
          </div>
        );
      }
    },
    {
      headerName: "Akcja",
      field: "action",
      minWidth: 110,
      lockPosition: 'left',
      headerClass: 'no-separator',
      cellStyle: function (params) {
        if (params.value === true) {
          return { backgroundColor: '#741b47', color: 'white' };
        }
        else if (!params.data.finishDate) {
          return { color: 'black' };
        }
      },
      cellRenderer: function (params) {
        return (
          <>
            <button
              className="table-action-btn"
              onClick={() => goToMonthlyStatement(params.data.accountantId, params.data.month, params.data.year)}
            >
              <i className="fas fa-search"></i>
              Sprawdź
            </button>
          </>
        );
      }
    },
    {
      headerName: "",
      field: "",
      minWidth: 110,
      lockPosition: 'left',
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: function (params) {
        const { approvalDate, finishDate, statementApproved } = params.data;
        const standardTerm = 9;

        if (statementApproved) {
          const daysDifference = CommonFunctions.calculateDaysDifference(finishDate, approvalDate);

          if (daysDifference <= standardTerm) {
            return (
              <span style={{ fontSize: "1.3em" }} title={`Zatwierdzone w terminie (${daysDifference} dni)`}>
                😊
              </span>
            );
          }
        } else if (finishDate) {
          const daysSinceFinish = CommonFunctions.calculateDaysDifference(finishDate, new Date());
          const daysRemaining = standardTerm - daysSinceFinish;

          if (daysRemaining > 0) {
            const msg = getRemainingDaysMessage(daysRemaining);
            return (
              <span style={{
                fontSize: "11px",
                fontWeight: "600",
                color: darkTheme ? "#ffcc00" : "#b78103",
                backgroundColor: darkTheme ? "rgba(255, 204, 0, 0.1)" : "#fffdeb",
                padding: "2px 8px",
                borderRadius: "12px",
                border: darkTheme ? "1px solid rgba(255, 204, 0, 0.3)" : "1px solid #ffeeba"
              }}>
                {msg}
              </span>
            );
          } else {
            return (
              <span style={{ fontSize: "1.3em" }} title="Termin zatwierdzenia minął!">
                ⚡
              </span>
            );
          }
        }

        return "";
      }
    },
    {
      headerName: "Faktura",
      field: "faktura",
      lockPosition: 'left',
      minWidth: 250,
      cellStyle: { display: 'flex', alignItems: 'center' },
      cellRenderer: function (params) {
        const fileMetadata = params.data.fileMetadataDTOS.find(
          (file) => file.fileType === 11
        );
        const fileMetadata71 = params.data.fileMetadataDTOS.find(
          (file) => file.fileType === 71
        );
        const fileMetadata81 = params.data.fileMetadataDTOS.find(
          (file) => file.fileType === 81
        );

        if (fileMetadata) {
          const salary = params.data.salary || 0;
          const isVatPayer = params.data.isVATPayer;
          const text = isVatPayer
            ? `${(salary * 1.23).toFixed(2)} zł (${salary.toFixed(2)} zł netto)`
            : `${salary.toFixed(2)} zł netto (zw.)`;

          const hasCorrection = !!(fileMetadata71 || fileMetadata81);

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "nowrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <i className="fas fa-coins" style={{ color: "#feb33c", fontSize: "14px" }}></i>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    CommonFunctions.downloadFile(fileMetadata.id, fileMetadata.fileType, params.data.accountantName, params.data.month, params.data.year, token);
                  }}
                  style={{
                    textDecoration: "underline",
                    color: darkTheme ? "#4fc3f7" : "#0068b3",
                    fontWeight: "500"
                  }}
                >
                  {hasCorrection ? "faktura" : text}
                </a>
              </div>
              {fileMetadata71 && (
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <i className="fas fa-file-invoice" style={{ color: "#e056fd", fontSize: "14px" }}></i>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      CommonFunctions.downloadFile(fileMetadata71.id, fileMetadata71.fileType, params.data.accountantName, params.data.month, params.data.year, token);
                    }}
                    style={{
                      textDecoration: "underline",
                      color: darkTheme ? "#e056fd" : "#8a2be2",
                      fontWeight: "500"
                    }}
                  >
                    korekta: {text}
                  </a>
                </div>
              )}
              {fileMetadata81 && (
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <i className="fas fa-file-invoice" style={{ color: "#e056fd", fontSize: "14px" }}></i>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      CommonFunctions.downloadFile(fileMetadata81.id, fileMetadata81.fileType, params.data.accountantName, params.data.month, params.data.year, token);
                    }}
                    style={{
                      textDecoration: "underline",
                      color: darkTheme ? "#e056fd" : "#8a2be2",
                      fontWeight: "500"
                    }}
                  >
                    korekta: {text}
                  </a>
                </div>
              )}
            </div>
          );
        } else {
          return null;
        }
      }
    },
    {
      headerName: "Oświadczenie",
      field: "",
      lockPosition: 'left',
      minWidth: 130,
      cellStyle: { display: 'flex', alignItems: 'center' },
      cellRenderer: function (params) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {params.data.isDeclarationSent ? (
              <>
                <i className="fas fa-file-signature" style={{ color: "#2ecc71", fontSize: "16px" }}></i>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    generatePDF(params.data.accountantId, params.data, params.data.month, params.data.year);
                  }}
                  style={{
                    textDecoration: "underline",
                    color: darkTheme ? "#4fc3f7" : "#0068b3",
                    fontWeight: "500"
                  }}
                >
                  oświadczenie
                </a>
              </>
            ) : null}
          </div>
        );
      }
    },
    {
      headerName: "Prace Dodatkowe",
      field: "additionalWorkDTO.additionalWorkDescription",
      tooltipValueGetter: (params) => params.data.additionalWorkDTO?.additionalWorkDescription,
      minWidth: 270,
      lockPosition: "left",
      cellStyle: { textAlign: "left" },
      cellRenderer: function (params) {
        const { data } = params;
        const isVatPayer = data.isVATPayer || false;
        const additionalWorkAmount = data.additionalWorkDTO?.additionalWorkAmount || 0;

        const fileMetadata = data.fileMetadataDTOS?.find((file) => file.fileType === 1);
        const fileMetadata2 = data.fileMetadataDTOS?.find((file) => file.fileType === 21);
        const fileMetadata71 = data.fileMetadataDTOS?.find((file) => file.fileType === 71);

        const createLink = (file, label, style = {}) => (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              CommonFunctions.downloadFile(file.id, file.fileType, data.accountantName, params.data.month, params.data.year, token);
            }}
            style={{ textDecoration: "underline", color: darkTheme ? "white" : "black", ...style }}
          >
            {label}
          </a>
        );

        let amountContent = null;
        let correctionLink = null;
        let sheetLink = null;

        if (data.additionalWorkDTO?.additionalWorkTypeId === 1 || data.additionalWorkDTO?.additionalWorkTypeId === 11) {
          const isVatFree = data.additionalWorkDTO?.additionalWorkTypeId === 11;
          const hasCorrection = !!fileMetadata71;
          const currentAmount = hasCorrection
            ? (data.additionalWorkDTO?.additionalWorkApprovedAmount || 0)
            : (data.additionalWorkDTO?.additionalWorkAmount || 0);

          const amountLabel = (isVatPayer && !isVatFree)
            ? (currentAmount * 1.23).toFixed(2) + " zł brutto"
            : currentAmount.toFixed(2) + " zł" + (isVatFree ? " (zw.)" : " netto");

          if (hasCorrection) {
            amountContent = (fileMetadata2 && !isVatFree) ? createLink(fileMetadata2, "faktura dodatkowa") : null;
            correctionLink = createLink(fileMetadata71, `korekta: ${amountLabel}`);
          } else {
            amountContent = (fileMetadata2 && !isVatFree) ? createLink(fileMetadata2, amountLabel) : <span>{amountLabel}</span>;
          }

          if (fileMetadata) {
            sheetLink = createLink(fileMetadata, "arkusz");
          }
        }

        if (!amountContent && !sheetLink && !correctionLink) {
          return null;
        }

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "15px", height: "100%" }}>
            {amountContent && (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <i className="fas fa-coins" style={{ marginRight: "4px", color: "#feb33c" }}></i>
                {amountContent}
              </span>
            )}
            {correctionLink && (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <i className="fas fa-file-invoice" style={{ marginRight: "4px", color: "#e056fd" }}></i>
                {correctionLink}
              </span>
            )}
            {sheetLink && (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <i className="fas fa-file-excel" style={{ marginRight: "4px", color: "#217346" }}></i>
                {sheetLink}
              </span>
            )}
          </div>
        );
      },
    },
    {
      headerName: "Uwagi do prac dodatkowych",
      field: "additionalWorkDTO.additionalWorkComment",
      lockPosition: 'left',
      minWidth: 150,
      cellStyle: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      tooltipValueGetter: (params) => params.value || '',
    },
    {
      headerName: "Data zatwierdzenia prac dodatkowych",
      field: "additionalWorkApprovalDate",
      lockPosition: 'left',
      filter: 'agDateColumnFilter',
      headerClass: 'no-separator',
      minWidth: 160,
      cellRenderer: function (params) {
        if (!params.data) return null;

        const { additionalWorkDTO } = params.data;

        if (
          additionalWorkDTO &&
          !additionalWorkDTO?.additionalWorkApprovalDate &&
          isApproveButtonAllowed
        ) {
          return (
            <div style={{ lineHeight: '1.7' }}>
              <button
                className="table-action-btn"
                style={{ width: '100%' }}
                onClick={() => handleOpenApproveDialog(params)}
              >
                <i className="fas fa-check"></i>
                Zatwierdź
              </button>
            </div>
          );
        }

        // Wyświetl datę zatwierdzenia lub pusty string, jeśli brak daty
        return additionalWorkDTO?.additionalWorkApprovalDate || '';
      },
      cellStyle: (params) => {
        const baseStyle = {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          color: darkTheme ? '#66ff66' : "green",
        };

        return baseStyle;
      }
    },
  ], [darkTheme]);

  const handleOpenApproveDialog = async (params) => {
    setCurrentRecordForApproval(params.data.additionalWorkDTO);
    setOpenApproveDialog(true);
  }

  const handleCloseApproveDialog = async () => {
    setOpenApproveDialog(false);
  }

  const handleConfirmApproval = async (approvedAmount, isApproved = true, comment = '') => {
    currentRecordForApproval.additionalWorkApprovalDate = CommonFunctions.convertDateToJavaFormat(currentDate);
    currentRecordForApproval.additionalWorkApprovedBy = contactId;
    currentRecordForApproval.isAdditionalWorkApproved = isApproved;
    currentRecordForApproval.additionalWorkApprovedAmount = isApproved ? approvedAmount : 0;
    currentRecordForApproval.additionalWorkComment = comment;
    updateAdditionalWork(urlAdditionalCosts, currentRecordForApproval.id, currentRecordForApproval, currentDate);
    logActivity((isApproved ? logActionType.APPROVE_ADDITIONAL_WORK : "Odrzucono prace dodatkowe dla ") + currentRecordForApproval.accountantName + ", data: " + currentDate, userName, token);
    setOpenApproveDialog(false);
  };

  const updateAdditionalWork = (url, id, dataToSend) => {
    fetch(url + `/additionalWork/${id}`, {
      method: "PUT",
      body: JSON.stringify(dataToSend),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    }).then(resp => resp.json())
      .catch(error => {
        console.error('Błąd podczas wysyłania rekordu accountingSettlement:', error);
      });
  }

  const goToMonthlyStatement = (accountantId, month, year) => {
    navigate(`/MonthlyStatement?accountantId=${accountantId}&month=${month}&year=${year}`);
  };

  useEffect(() => {
    fetchAccountingSettlement(selectedMonth, selectedYear, selectedSupervisor)
    getSupervisors();
  }, []);

  const onGridReady = (params) => {
    setGridApi(params)
  }

  const defaultColDef = {
    resizable: true,
    editable: false,
    filter: false,
    flex: 1,
    tooltipComponent: CustomTooltip,
  }

  const mapAccountingSettlement = (item) => {
    return {
      ...item,
      generateInvoiceCheckbox: false,
      payedCheckbox: false,
      generatedInvoices: item.generatedInvoices ? 'wygenerowano' : 'oczekuje...',
    };
  };

  const generatePDF = async (accountantId, data, month, year) => {
    // Pobranie danych o klientach na podstawie accountantId
    const clients = await fetchClientsByAccountant(accountantId, month, year);

    // Generowanie zawartości PDF z danymi klientów
    const content = ReactDOMServer.renderToString(<MyPDFContent clients={clients} data={data} />);

    const element = document.createElement('div');
    element.innerHTML = content;

    const opt = {
      margin: 1,
      filename: data.accountantName + `_oswiadczenie_${month}_${year}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    html2pdf()
      .from(element)
      .set(opt)
      .save()
      .catch(err => {
        console.error('Błąd podczas generowania PDF:', err);
      });
  };

  const fetchClientsByAccountant = async (accountantId, month, year) => {
    try {
      // Wywołanie funkcji fetchDataPromise, aby pobrać klientów na podstawie accountantId
      const url = urlAccountingStatements + `/clients?accountantId=${accountantId}&month=${month}&year=${year}`;
      const clients = await fetchDataPromise(url, null, setClients, null, token);
      return clients;
    } catch (error) {
      console.error('Błąd pobierania klientów:', error);
      return []; // Zwróć pustą tablicę w przypadku błędu
    }
  };

  const handleSupervisorChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedSupervisor(selectedValue);
    fetchAccountingSettlement(selectedMonth, selectedYear, selectedValue);
  };

  const handleMonthChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedMonth(selectedValue);
    fetchAccountingSettlement(selectedValue, selectedYear, selectedSupervisor);
  };

  const handleYearChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedYear(selectedValue);
    fetchAccountingSettlement(selectedMonth, selectedValue, selectedSupervisor);
  };

  function getRemainingDaysMessage(daysRemaining) {
    if (daysRemaining <= 0) {
      return "";
    }

    let dayWord;
    let verbForm;

    if (daysRemaining === 1) {
      dayWord = "dzień";
      verbForm = "został";
    } else if (daysRemaining >= 2 && daysRemaining <= 4) {
      dayWord = "dni";
      verbForm = "zostały";
    } else {
      dayWord = "dni";
      verbForm = "zostało";
    }

    return `${verbForm} ${daysRemaining} ${dayWord}`;
  }


  const getSupervisors = () => {
    fetch(urlSupervisors + "/allForSelect", { headers: { 'Authorization': `Bearer ${token}` } })
      .then(resp => resp.json())
      .then(data => {
        const rowData = data.map(item => {
          return {
            id: item.id,
            title: item.surname + ' ' + item.name,
          };
        });

        // Dodaj statyczny element na początku listy
        const updatedData = [{ id: 0, title: "Wszyscy Superwizorzy" }, ...rowData];

        setSupervisors(updatedData);
      })
      .catch(error => {
        console.error('Błąd pobierania danych:', error);
      });
  };

  const fetchAccountingSettlement = (month, year, supervisor) => {
    const selectedMonth = MyCollections.getMonthCollection().find(
      (item) => item.id === month
    );

    const monthParam = selectedMonth ? selectedMonth.id : '';

    const url = urlAccountingSettlement + `/all?supervisorId=${supervisor}&month=${monthParam}&year=${year}`;
    setUrl(url);

    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(response => response.json())
      .then(data => {
        setAccountingSettlement(data);
        const rowData = data.map(mapAccountingSettlement);
        setTableData(rowData)
      })
      .catch(error => console.error(error));
  };

  useEffect(() => {
    const defaultLim = parseInt(CommonFunctions.getMonthForSelect(selectedYear), 10);
    const currentSelMonth = parseInt(selectedMonth, 10);
    setUnlockedMonthLimit(Math.max(defaultLim, currentSelMonth));
  }, [selectedYear, selectedSupervisor]);

  useEffect(() => {
    if (Array.isArray(accountingSettlement) && accountingSettlement.some(item => item.statementFinished)) {
      const finishedMonth = parseInt(selectedMonth, 10);
      const nextMonth = finishedMonth + 1;
      setUnlockedMonthLimit(prev => Math.max(prev, nextMonth));
    }
  }, [accountingSettlement, selectedMonth]);


  return (
    <>
      <Paper className={classes.pageContent} elevation={0} >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap="12px"
          sx={{ mb: 2, px: 2, pt: 2 }}
        >
          {/* Lewa strona: Filtry */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
            <Box minWidth={160}>
              <Controls.MySelect
                name="monthId"
                label="Zestawienie za miesiąc: "
                value={selectedMonth}
                onChange={handleMonthChange}
                options={MyCollections.getMonthCollection(unlockedMonthLimit)}
              />
            </Box>
            <Box minWidth={100}>
              <Controls.MySelect
                name="yearId"
                label="Rok: "
                value={selectedYear}
                onChange={handleYearChange}
                options={MyCollections.getYearCollection(
                  2023,
                  Math.max(
                    parseInt(CommonFunctions.getYearForSelect(), 10),
                    unlockedMonthLimit > 12 ? parseInt(selectedYear, 10) + 1 : parseInt(CommonFunctions.getYearForSelect(), 10)
                  )
                )}
              />
            </Box>
            {(roles?.includes('ADMIN') || roles?.includes('ADMIN_SUPERVISOR') || roles?.includes('MANAGER')) && (
              <Box minWidth={180}>
                <Controls.MySelect
                  name="supervisorId"
                  label="Superwizor: "
                  value={selectedSupervisor}
                  onChange={handleSupervisorChange}
                  options={supervisors}
                />
              </Box>
            )}
          </Box>

          {/* Prawa strona: Przyciski akcji */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gap="8px">
          </Box>
        </Box>
        <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }}>
          <Grid item xs={12}>
            <div className="App">
              <div className="tableData">
                {/* On div wrapping Grid a) specify theme CSS Class Class and b) sets Grid size */}
                <div
                  className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                  style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                >
                  <AgGridReact
                    key={darkTheme ? 'grid-dark' : 'grid-light'}
                    rowData={tableData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    pagination={true}
                    paginationPageSize={200}
                    suppressBrowserResizeObserver={true}
                    animateRows={true}
                    rowSelection={false}
                    suppressSizeToFit={true}
                    enableBrowserTooltips={false}
                    localeText={MyCollections.localeTextPL}
                    tooltipShowDelay={0}
                    popupParent={document.body}
                    overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                    overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                  />
                </div>
              </div>
            </div>
          </Grid>
          <ApproveAdditionalCosts open={openApproveDialog} handleClose={handleCloseApproveDialog} isDiscount={false} handleConfirmApproval={handleConfirmApproval} contactName={currentRecordForApproval.accountantName}
            signature={userName} date={currentDate} amount={currentRecordForApproval.additionalWorkAmount} costDescription={currentRecordForApproval.additionalWorkDescription} darkTheme={darkTheme} gross={currentRecordForApproval.isVATPayer && currentRecordForApproval.additionalWorkTypeId !== 11} />
        </Grid>
      </Paper>
    </>
  );
}

export default ApproveStatements;